import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';

import { status } from 'constants/general';
import advertsStrings from 'modules/adverts/strings';
import commonStrings from 'modules/common/strings';
import DropdownMenu from 'modules/common/components/DropdownMenu';
import MultiSelectionDropdownMenu from 'modules/common/components/MultiSelectionDropdownMenu';
import AutoCompleteIsolated from 'modules/common/components/AutoCompleteIsolated';
import SingleFileUploader from 'modules/common/components/SingleFileUploader';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  LoaderWrapper,
  AddAdvertWrapper,
  FormWrapper,
  FormTitle,
  FormGrid,
  FormLabel,
  FormMultiFieldWrapper,
  FormMultiFieldInputWrapper,
  FormTextInput,
  FormFieldWrapper,
  FormValidationError,
  FormBtnWrapper,
  FormBtn,
  /* ####################### */
  EnvelopItemWrapper,
  EnvelopItemImage,
  EnvelopItemNameWrapper,
  EnvelopItemName,
  EnvelopItemType,
  EnvelopItemPriceWrapper,
  EnvelopItemPrice,
  EnvelopItemStock,
  EnvelopLoaderWrapper,
  NoDataText,
  SelectedProduct,
  SelectedProductCloseIcon,
} from './AddAdvertComponent.style';

const SearchProductsEnvelop = ({
  searchProductsInfo,
  handleProductItemClick,
  searchProductItemClickFunc,
}) => {
  const renderSearchProductsResults = () => {
    const {
      details: { productShapes: data },
    } = searchProductsInfo;

    return data.map(result => (
      <EnvelopItemWrapper
        key={result.id}
        onClick={() => handleProductItemClick(result, searchProductItemClickFunc)}
      >
        <EnvelopItemImage src={result.productShapeIconUrl} />
        <EnvelopItemNameWrapper>
          <EnvelopItemName>{result.productNameEn}</EnvelopItemName>
          <EnvelopItemType>{result.productShapeTypeName}</EnvelopItemType>
        </EnvelopItemNameWrapper>
        <EnvelopItemPriceWrapper>
          <EnvelopItemPrice>{`${result.newPrice} ${result.currencyEn}`}</EnvelopItemPrice>
          {result.stockQuantity === 0 && (
            <EnvelopItemStock>
              {commonStrings.searchProductsAutoComplete.searchProductEnvelopOutOfStockText}
            </EnvelopItemStock>
          )}
        </EnvelopItemPriceWrapper>
      </EnvelopItemWrapper>
    ));
  };

  const renderSearchProductsEnvelop = () => {
    const { status: autocompleteStatus, details } = searchProductsInfo;
    const data = details?.productShapes;

    if (autocompleteStatus === status.SHOULD_CALL_API || autocompleteStatus === status.FETCHING)
      return (
        <EnvelopLoaderWrapper>
          <Loader width={30} height={30} thickness={5} />
        </EnvelopLoaderWrapper>
      );

    if (
      autocompleteStatus === status.FAIL ||
      (autocompleteStatus === status.SUCCESS && (!data || data?.length === 0))
    )
      return (
        <EnvelopLoaderWrapper>
          <NoDataText>
            {commonStrings.searchProductsAutoComplete.searchProductEnvelopNoResultsText}
          </NoDataText>
        </EnvelopLoaderWrapper>
      );

    return renderSearchProductsResults();
  };

  return renderSearchProductsEnvelop();
};
SearchProductsEnvelop.propTypes = {
  searchProductsInfo: PropTypes.shape({}).isRequired,
  handleProductItemClick: PropTypes.func.isRequired,
  searchProductItemClickFunc: PropTypes.func.isRequired,
};
/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */
const AddAdvertComponent = ({
  shouldShowLandingField,
  shouldShowImageField,
  shouldShowProductField,
  formFieldsValues,
  formFieldsValidation,
  getSearchProductsResults,
  resetSearchProductsResults,
  handleTextInputFieldChange,
  handleDropdownFieldChange,
  handleMultiSelectionDropdownFieldChange,
  handleUploadFileChange,
  handleProductItemClick,
  handleProductItemDeleteIconClick,
  handleAddOrEditAdvertBtnClick,
}) => {
  const { campaignInfo, addOrEditAdvert, searchProductsInfo, staticData } = useSelector(state => ({
    campaignInfo: state.campaigns.campaignInfo,
    addOrEditAdvert: state.adverts.addOrEditAdvert,
    searchProductsInfo: state.common.searchProductsInfo,
    staticData: state.common.staticData,
  }));

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const landingTypeDropdownData =
    staticData.details?.data?.landingPages?.map(landingPage => ({
      key: landingPage.landingPage,
      name: landingPage.landingPage,
      landingPageUrl: landingPage.landingPageUrl,
      adLandingPageId: landingPage.adLandingPageId,
    })) || [];

  const selectedLandingTypeObj =
    landingTypeDropdownData.find(landingType => landingType.key === formFieldsValues.landingType) ||
    {};

  const landingPageUrlPlaceholder = selectedLandingTypeObj?.landingPageUrl || '';

  const zoneDropdownData = campaignInfo.details?.zones?.map(zone => ({
    key: zone.zoneKey,
    name: zone.zoneName,
  }));

  if (campaignInfo.status === status.SHOULD_CALL_API || campaignInfo.status === status.FETCHING)
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );

  return (
    <AddAdvertWrapper>
      <FormWrapper>
        <FormTitle>{advertsStrings.addAdvert.formTitle}</FormTitle>
        <SectionDivider gap={16} withSeparator separatorGap={16} borderColor={colors.grey004} />
        <FormGrid>
          {/* Name Field */}
          <FormFieldWrapper>
            <FormLabel>{advertsStrings.addAdvert.formNameFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <FormTextInput
              type="text"
              value={formFieldsValues.name}
              onChange={event => handleTextInputFieldChange('name', event)}
            />
            {formFieldsValidation.name === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {advertsStrings.addAdvert.formFieldErrorText(
                    advertsStrings.addAdvert.formNameFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Zone Field */}
          <FormFieldWrapper>
            <FormLabel>{advertsStrings.addAdvert.formZoneFieldLabel}</FormLabel>
            <SectionDivider gap={8} />
            <MultiSelectionDropdownMenu
              dropdownMenuItemsList={zoneDropdownData}
              dropdownMenuSelectedArr={formFieldsValues.zone}
              dropdownMenuPlaceholder={advertsStrings.addAdvert.formZoneFieldPlaceholder}
              handleDropdownMenuChange={value =>
                handleMultiSelectionDropdownFieldChange('zone', value)
              }
            />
            {formFieldsValidation.zone === false && (
              <>
                <SectionDivider gap={4} />
                <FormValidationError>
                  {advertsStrings.addAdvert.formFieldErrorText(
                    advertsStrings.addAdvert.formZoneFieldLabel,
                  )}
                </FormValidationError>
              </>
            )}
          </FormFieldWrapper>

          {/* Landing Field */}
          {shouldShowLandingField && (
            <FormFieldWrapper fullWidth>
              <FormLabel>{advertsStrings.addAdvert.formLandingPageFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <FormMultiFieldWrapper>
                <FormMultiFieldInputWrapper flexRatio={1}>
                  <DropdownMenu
                    dropdownMenuItemsList={landingTypeDropdownData}
                    dropdownMenuSelectedObj={selectedLandingTypeObj}
                    dropdownMenuPlaceholder={
                      advertsStrings.addAdvert.formLandingTypeFieldPlaceholder
                    }
                    showLoader={false}
                    handleDropdownMenuChange={value =>
                      handleDropdownFieldChange('landingType', value)
                    }
                  />
                  {formFieldsValidation.landingType === false && (
                    <>
                      <SectionDivider gap={4} />
                      <FormValidationError>
                        {advertsStrings.addAdvert.formFieldErrorText(
                          advertsStrings.addAdvert.formLandingTypeFieldLabel,
                        )}
                      </FormValidationError>
                    </>
                  )}
                </FormMultiFieldInputWrapper>
                <FormMultiFieldInputWrapper flexRatio={2}>
                  <FormTextInput
                    type="text"
                    value={formFieldsValues.landingValue}
                    placeholder={landingPageUrlPlaceholder}
                    onChange={event => handleTextInputFieldChange('landingValue', event)}
                    onFocus={event => {
                      // If input is empty and has a placeholder, fill it so user can copy
                      if (!event.target.value && landingPageUrlPlaceholder) {
                        handleTextInputFieldChange('landingValue', {
                          target: { value: landingPageUrlPlaceholder },
                        });
                        // Select the text so user can easily copy or replace
                        setTimeout(() => event.target.select(), 0);
                      }
                    }}
                  />
                  {formFieldsValidation.landingValue === false && (
                    <>
                      <SectionDivider gap={4} />
                      <FormValidationError>
                        {advertsStrings.addAdvert.formFieldErrorText(
                          advertsStrings.addAdvert.formLandingValueFieldLabel,
                        )}
                      </FormValidationError>
                    </>
                  )}
                </FormMultiFieldInputWrapper>
              </FormMultiFieldWrapper>
            </FormFieldWrapper>
          )}

          {/* Image Field */}
          {shouldShowImageField && (
            <FormFieldWrapper fullWidth>
              <FormLabel>{advertsStrings.addAdvert.formImageFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <SingleFileUploader
                fileData={formFieldsValues.imageData}
                allowMultipleFilesUpload={false}
                setUploadedData={handleUploadFileChange}
              />
              {formFieldsValidation.imageData === false && (
                <>
                  <SectionDivider gap={4} />
                  <FormValidationError centerText>
                    {advertsStrings.addAdvert.formFieldErrorText(
                      advertsStrings.addAdvert.formImageFieldLabel,
                    )}
                  </FormValidationError>
                </>
              )}
            </FormFieldWrapper>
          )}

          {/* Product Field */}
          {shouldShowProductField && (
            <FormFieldWrapper fullWidth>
              <FormLabel>{advertsStrings.addAdvert.formProductFieldLabel}</FormLabel>
              <SectionDivider gap={8} />
              <AutoCompleteIsolated
                // eslint-disable-next-line react/no-unstable-nested-components
                getEnvelopContent={searchProductItemClickFunc => (
                  <SearchProductsEnvelop
                    searchProductsInfo={searchProductsInfo}
                    handleProductItemClick={handleProductItemClick}
                    searchProductItemClickFunc={searchProductItemClickFunc}
                  />
                )}
                getSearchQueryResults={getSearchProductsResults}
                resetSearchQueryResults={resetSearchProductsResults}
              />
              {formFieldsValues.products &&
                formFieldsValues.products.map(product => (
                  <SelectedProduct key={product.productKey}>
                    {product.productNameEn}
                    <SelectedProductCloseIcon
                      onClick={() => handleProductItemDeleteIconClick(product)}
                    />
                  </SelectedProduct>
                ))}
              {formFieldsValidation.products === false && (
                <>
                  <SectionDivider gap={4} />
                  <FormValidationError>
                    {advertsStrings.addAdvert.formFieldErrorText(
                      advertsStrings.addAdvert.formProductFieldLabel,
                    )}
                  </FormValidationError>
                </>
              )}
            </FormFieldWrapper>
          )}
        </FormGrid>
        <SectionDivider gap={35} />
        <FormBtnWrapper>
          <FormBtn as="button" onClick={handleAddOrEditAdvertBtnClick}>
            {(addOrEditAdvert.status === status.FETCHING && (
              <Loader width={14} height={14} thickness={4} />
            )) ||
              advertsStrings.addAdvert.formBtnText}
          </FormBtn>
        </FormBtnWrapper>
      </FormWrapper>
    </AddAdvertWrapper>
  );
};

AddAdvertComponent.propTypes = {
  shouldShowLandingField: PropTypes.bool.isRequired,
  shouldShowImageField: PropTypes.bool.isRequired,
  shouldShowProductField: PropTypes.bool.isRequired,
  formFieldsValues: PropTypes.shape({
    name: PropTypes.string,
    landingType: PropTypes.string,
    landingValue: PropTypes.string,
    zone: PropTypes.arrayOf(PropTypes.shape({})),
    imageData: PropTypes.shape({
      fileName: PropTypes.string,
    }),
    products: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
  formFieldsValidation: PropTypes.shape({
    name: PropTypes.bool,
    landingType: PropTypes.bool,
    landingValue: PropTypes.bool,
    zone: PropTypes.bool,
    imageData: PropTypes.bool,
    products: PropTypes.bool,
  }).isRequired,
  getSearchProductsResults: PropTypes.func.isRequired,
  resetSearchProductsResults: PropTypes.func.isRequired,
  handleTextInputFieldChange: PropTypes.func.isRequired,
  handleDropdownFieldChange: PropTypes.func.isRequired,
  handleMultiSelectionDropdownFieldChange: PropTypes.func.isRequired,
  handleUploadFileChange: PropTypes.func.isRequired,
  handleProductItemClick: PropTypes.func.isRequired,
  handleProductItemDeleteIconClick: PropTypes.func.isRequired,
  handleAddOrEditAdvertBtnClick: PropTypes.func.isRequired,
};

export default AddAdvertComponent;
